import type { TFunction } from 'i18next';
import type { BreadcrumbItem } from '@/components/layout/Breadcrumbs';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleBreadcrumbLabel, getModuleEntryByErpModule } from '@/config/moduleEntries';
import { SETTINGS_PATH, getSettingsCategory } from '@/config/settingsRegistry';
import { resolveRoutePageTitle, getEnglishRoutePageTitle } from '@/config/routePageTitles';
import { i18n } from '@/i18n';

/** Maps a route path to a key within the `navigation` namespace (e.g. paths.pos.products). */
export function pathToNavKey(path: string): string {
  const normalized = path.split('?')[0].replace(/^\//, '').replace(/\//g, '.');
  return normalized ? `paths.${normalized}` : 'paths.root';
}

function normalizeBreadcrumbPath(pathname: string): string {
  const base = pathname.split('?')[0].replace(/\/+$/, '');
  return base || '/';
}

/** Module workspace section labels used in ModuleSectionLayout breadcrumbs. */
const SECTION_LABEL_TO_GROUP: Record<string, string> = {
  Dashboard: 'groups.dashboard',
  Operations: 'groups.operations',
  Reports: 'groups.reports',
  Setup: 'groups.setup',
  Administration: 'groups.administration',
};

/** Work hub breadcrumb labels. */
const WORK_BREADCRUMB_KEYS: Record<string, string> = {
  'My Work': 'work.myWork',
  Notifications: 'work.notifications',
  Executive: 'work.executiveView',
};

export function resolveNavLabel(path: string, fallback: string, t: TFunction): string {
  return t(pathToNavKey(path), { ns: 'navigation', defaultValue: fallback });
}

export function resolveGroupLabel(groupId: string, fallback: string, t: TFunction): string {
  return t(`groups.${groupId}`, { ns: 'navigation', defaultValue: fallback });
}

/** Localizes ModuleSectionLayout section labels (Dashboard, Operations, etc.). */
export function resolveSectionLabel(section: string, t: TFunction): string {
  const groupKey = SECTION_LABEL_TO_GROUP[section];
  if (groupKey) {
    return t(groupKey, { ns: 'navigation', defaultValue: section });
  }
  return section;
}

/** Maps ERP module codes to navigation.json `modules.*` keys (e.g. LEAD_GEN → LEADGEN). */
export function moduleNavigationKey(module: string): string {
  if (module === 'LEAD_GEN') return 'LEADGEN';
  return module;
}

export function localizeModuleLabel(
  module: string,
  t?: TFunction,
  fallback?: string,
): string {
  const english =
    fallback ?? getModuleBreadcrumbLabel(module) ?? getModuleEntryByErpModule(module)?.label ?? module;
  const key = moduleNavigationKey(module);
  if (t) {
    return t(`modules.${key}`, { ns: 'navigation', defaultValue: english });
  }
  if (i18n.isInitialized) {
    return i18n.t(`modules.${key}`, { ns: 'navigation', defaultValue: english });
  }
  return english;
}

function isModuleHomePath(path: string): boolean {
  const module = resolveModuleFromPath(path);
  if (!module) return false;
  const home = getModuleEntryByErpModule(module)?.homePath;
  return home != null && normalizeBreadcrumbPath(home) === normalizeBreadcrumbPath(path);
}

export function localizeBreadcrumbItem(
  item: BreadcrumbItem,
  t: TFunction,
  options?: { pathname?: string; isLast?: boolean },
): BreadcrumbItem {
  if (item.to) {
    const path = normalizeBreadcrumbPath(item.to);
    const [pathOnly, hash] = path.split('#');
    if (hash && pathOnly === SETTINGS_PATH) {
      const category = getSettingsCategory(hash);
      if (category) {
        return { ...item, label: category.label };
      }
    }
    if (pathOnly === SETTINGS_PATH) {
      return { ...item, label: resolveNavLabel('settings', item.label, t) };
    }
    if (isModuleHomePath(pathOnly)) {
      const module = resolveModuleFromPath(pathOnly)!;
      const compact =
        getModuleBreadcrumbLabel(module) ??
        getModuleEntryByErpModule(module)?.breadcrumbLabel ??
        item.label;
      const key = `breadcrumbModules.${moduleNavigationKey(module)}`;
      const localized = t(key, { ns: 'navigation', defaultValue: compact });
      return { ...item, label: localized || compact };
    }
    const pathKey = pathOnly.replace(/^\//, '');
    return { ...item, label: resolveNavLabel(pathKey, item.label, t) };
  }

  if (options?.isLast && options.pathname) {
    const fromRoute = resolveRoutePageTitle(options.pathname);
    if (fromRoute) return { ...item, label: fromRoute };
  }

  const sectionKey = SECTION_LABEL_TO_GROUP[item.label];
  if (sectionKey) {
    return { ...item, label: t(sectionKey, { ns: 'navigation', defaultValue: item.label }) };
  }

  const workKey = WORK_BREADCRUMB_KEYS[item.label];
  if (workKey) {
    return { ...item, label: t(workKey, { ns: 'navigation', defaultValue: item.label }) };
  }

  return item;
}

export function localizeBreadcrumbItems(
  items: BreadcrumbItem[],
  t: TFunction,
  pathname?: string,
): BreadcrumbItem[] {
  return items.map((item, index) =>
    localizeBreadcrumbItem(item, t, {
      pathname,
      isLast: index === items.length - 1,
    }),
  );
}

/**
 * Resolves the visible page heading: prefers localized navigation label for the
 * current route when the explicit title is missing or still in English.
 */
export function resolvePageHeading(
  explicitTitle: string | undefined,
  pathname: string,
): string {
  const normalized = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  const routeTitle = resolveRoutePageTitle(normalized);
  const englishRouteTitle = getEnglishRoutePageTitle(normalized);
  const trimmed = explicitTitle?.trim();

  if (!trimmed) {
    return routeTitle ?? '';
  }

  if (routeTitle) {
    if (englishRouteTitle && trimmed.toLowerCase() === englishRouteTitle.toLowerCase()) {
      return routeTitle;
    }
    if (i18n.isInitialized) {
      const pathKey = pathToNavKey(normalized.replace(/^\//, ''));
      const enNavTitle = i18n.t(pathKey, { ns: 'navigation', lng: 'en_US', defaultValue: '' });
      if (enNavTitle && trimmed.toLowerCase() === enNavTitle.toLowerCase()) {
        return routeTitle;
      }
      // French UI: prefer nav label when heading still contains English fragments
      const lang = i18n.language ?? '';
      if (lang.startsWith('fr') && /\b[a-z]{4,}\b/.test(trimmed)) {
        return routeTitle;
      }
    }
  }

  return explicitTitle ?? '';
}

export function resolveBackendLabel(
  labelKey: string | undefined | null,
  label: string | undefined | null,
  t: TFunction,
): string {
  if (labelKey) {
    if (labelKey.startsWith('navigation.')) {
      const subKey = labelKey.slice('navigation.'.length);
      return t(subKey, { ns: 'navigation', defaultValue: label ?? labelKey });
    }
    return t(labelKey, { defaultValue: label ?? labelKey });
  }
  return label ?? '';
}

/** Resolves a page title from the navigation namespace (works outside React). */
export function resolveLocalizedPageTitle(pathname: string, fallback?: string): string {
  const base = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  const pathKey = base === '/' ? '' : base.replace(/^\//, '');
  const key = pathToNavKey(pathKey);
  if (i18n.isInitialized) {
    const localized = i18n.t(key, { ns: 'navigation', defaultValue: '' });
    if (localized) return localized;
  }
  return fallback ?? '';
}
